<script setup lang="ts">
/**
 * PublicChatWidget — chat support flottant pour visiteurs anonymes de
 * donoor.org. Remplace l'ancien bouton SupportDonoor : au lieu d'un simple
 * lien de contact, un vrai chat temps réel avec l'équipe (via usePublicChatController →
 * endpoints publics /api/public/chat/*, socket anonyme).
 *
 * Contraintes respectées :
 *  - client-only + import du composable au montage → aucun impact SSR / LCP.
 *  - light mode uniquement (donoor.org force color-mode light) — pas de dark:*.
 *  - même coin bas-droite / z-index que l'ancien SupportDonoor.
 */
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from "vue";
import { usePublicChatController } from "~/composables/usePublicChatController";
import { useRegisterLink } from "~/composables/useRegisterLink";
import { useChatPageContext } from "~/composables/useChatPageContext";
import { useChatDecalageBas } from "~/composables/useChatDecalageBas";
import { useQuestionPourChat } from "~/composables/useQuestionPourChat";
import { bulleAccueilAutorisee } from "~/utils/bulleAccueilChat";
import { openWhatsAppContact } from "~/utils/whatsapp";
import { decouperLiens } from "@donoor/utils/string";

const chat = usePublicChatController();
const draft = ref("");
const name = ref("");
const email = ref("");
const scrollEl = ref<HTMLElement | null>(null);
const champSaisie = ref<HTMLTextAreaElement | null>(null);
// Honeypot anti-bot (masqué CSS) — un humain ne le remplit jamais.
const website = ref("");

/**
 * Bulle d'accueil de la mascotte : une fois par visite, 4 s après l'arrivée,
 * puis plus rien pendant 24 h — même en ouvrant un nouvel onglet entre-temps
 * (Karim 2026-10-13 : `sessionStorage`, propre à CHAQUE onglet, la refaisait
 * apparaître à chaque nouvel onglet dans la même visite). `localStorage` +
 * horodatage : partagé entre les onglets, et elle réapparaît à la visite
 * suivante (le lendemain) plutôt que plus jamais.
 */
const GREETING_SESSION_KEY = "donoor_chat_greeted_at";
const GREETING_REAPPARAIT_APRES_MS = 24 * 60 * 60 * 1000;

const showGreeting = ref(false);
let greetingTimer: ReturnType<typeof setTimeout> | undefined;

// Stockage bloqué (navigation privée Safari, cookies refusés) : la bulle reste
// simplement sans mémoire, le chat fonctionne normalement.
function marquerVisiteSaluee() {
  try {
    localStorage.setItem(GREETING_SESSION_KEY, String(Date.now()));
  } catch {
    return;
  }
}

function dejaSalueCetteVisite(): boolean {
  try {
    const horodatage = Number(localStorage.getItem(GREETING_SESSION_KEY));
    return Boolean(horodatage) && Date.now() - horodatage < GREETING_REAPPARAIT_APRES_MS;
  } catch {
    return false;
  }
}

function dismissGreeting() {
  showGreeting.value = false;
  clearTimeout(greetingTimer);
  greetingTimer = undefined;
  marquerVisiteSaluee();
}

/**
 * Sautillement permanent de la mascotte (hors bulle d'accueil), pour qu'on la
 * remarque même après les 4 s de bienvenue (Karim 2026-10-12). UN saut à la
 * fois, à intervalle ALÉATOIRE (4 à 9 s) — en boucle CSS fixe, l'œil repère
 * tout de suite le rythme parfaitement régulier et ça lasse.
 */
const sautille = ref(false);
let sautTimer: ReturnType<typeof setTimeout> | undefined;
const DUREE_SAUT_MS = 1100; // doit matcher la durée de l'animation CSS `.mascotte--saute`

function planifierSaut() {
  const delai = 4000 + Math.random() * 5000;
  sautTimer = setTimeout(() => {
    sautille.value = true;
    sautTimer = setTimeout(() => {
      sautille.value = false;
      planifierSaut();
    }, DUREE_SAUT_MS);
  }, delai);
}

/**
 * Mascotte BLANCHE (yeux noirs) quand elle passe sur le hero photo de
 * l'accueil ou le footer noir, NOIRE (yeux blancs) partout ailleurs : sur un
 * fond clair une boule blanche disparaîtrait, et inversement. Le basculement se
 * fait quand le CENTRE de la bulle est réellement au-dessus de la zone (en
 * hauteur ET en largeur : un bloc sombre qui s'arrête avant le bord droit ne
 * compte pas). Mesure à la frame d'animation, zones recherchées à chaque
 * changement de page : le widget, lui, reste monté.
 */
const ZONES_SOMBRES = ".hero, footer.bg-black";
const surFondSombre = ref(false);
let zonesSombres: HTMLElement[] = [];
let rafId = 0;
const fabEl = ref<HTMLElement | null>(null);

/**
 * Place réservée sous la bulle par un élément flottant de la page (panier de
 * la fiche produit) : le chat se pose au-dessus au lieu de le recouvrir
 * (Karim 2026-10-17, cf. useChatDecalageBas).
 */
const decalageBas = useChatDecalageBas();
const styleDecalage = computed(() =>
  decalageBas.value ? { "--pcw-decalage": decalageBas.value } : undefined,
);
watch(decalageBas, onScroll, { flush: "post" });

function measure() {
  rafId = 0;
  // Centre RÉEL de la bulle : elle n'est pas toujours au même endroit
  // (décalage ci-dessus). Valeurs fixes en secours avant le montage.
  const r = fabEl.value?.getBoundingClientRect();
  const y = r ? r.top + r.height / 2 : window.innerHeight - 24 - 32;
  const x = r ? r.left + r.width / 2 : window.innerWidth - 24 - 32;
  surFondSombre.value = zonesSombres.some((zone) => {
    const r = zone.getBoundingClientRect();
    return r.top <= y && r.bottom >= y && r.left <= x && r.right >= x;
  });
}

function onScroll() {
  if (!rafId) rafId = requestAnimationFrame(measure);
}

async function trouverZonesSombres() {
  await nextTick();
  zonesSombres = Array.from(document.querySelectorAll<HTMLElement>(ZONES_SOMBRES));
  measure();
}

// Notification prioritaire d'abord : un message non lu passe avant « l'équipe écrit ».
// Indépendante de `surFondSombre` : la boule garde sa couleur habituelle, seuls
// les yeux signalent l'alerte (voir DonoorMascotte).
const alerteMascotte = computed(() => {
  if (chat.unread.value > 0) return "message";
  if (chat.staffTyping.value && !chat.open.value) return "ecrit";
  return undefined;
});

const route = useRoute();
watch(() => route.path, trouverZonesSombres);

// Bulle d'accueil « Vous gérez une association ? » : jamais sur une page vue
// par un donateur (cagnotte, billetterie, adhésion, association… — Karim
// 2026-10-16), ni sur une page qui a posé son contexte de fiche pour l'IA.
const { contexte: contexteFiche } = useChatPageContext();
const bulleAutorisee = computed(
  () => !contexteFiche.value && bulleAccueilAutorisee(route.path),
);

/** Programme la bulle 4 s après l'arrivée sur une page qui l'autorise. */
function planifierBulle() {
  if (greetingTimer || showGreeting.value || !bulleAutorisee.value || dejaSalueCetteVisite()) return;
  greetingTimer = setTimeout(() => {
    greetingTimer = undefined;
    if (chat.open.value || !bulleAutorisee.value) return;
    showGreeting.value = true;
    marquerVisiteSaluee();
  }, 4000);
}

// On passe sur une page donateur : la bulle (affichée ou programmée) s'efface,
// sans compter comme « vue » ; elle pourra venir sur une page qui l'autorise.
watch(bulleAutorisee, (autorisee) => {
  if (autorisee) return planifierBulle();
  clearTimeout(greetingTimer);
  greetingTimer = undefined;
  showGreeting.value = false;
});

/**
 * Demande d'ouverture envoyée par une autre partie du site (recherche du
 * centre d'aide, bouton « Nous écrire ») : on ouvre le chat, avec la question
 * déjà tapée si une vraie question a été fournie (chaîne vide = juste ouvrir,
 * sans rien écrire dans le champ). Le widget s'hydrate « à l'idle » : une
 * demande posée avant son montage est reprise dans onMounted, une fois
 * l'identité du visiteur prête.
 */
const questionPourChat = useQuestionPourChat();

async function prendreQuestionDemandee() {
  if (questionPourChat.value === null) return;
  const question = questionPourChat.value.trim();
  questionPourChat.value = null;
  if (question) draft.value = question;
  if (!chat.open.value) await chat.openPanel();
  await nextTick();
  champSaisie.value?.focus();
}

watch(questionPourChat, prendreQuestionDemandee);

onMounted(() => {
  chat.init();
  prendreQuestionDemandee();
  trouverZonesSombres();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  planifierBulle();

  planifierSaut();
});

onBeforeUnmount(() => {
  chat.dispose();
  if (greetingTimer) clearTimeout(greetingTimer);
  if (sautTimer) clearTimeout(sautTimer);
  if (rafId) cancelAnimationFrame(rafId);
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", onScroll);
});

async function scrollToBottom() {
  await nextTick();
  if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight;
}
watch(() => chat.messages.value.length, scrollToBottom);
// Fil rechargé (réouverture, rafraîchissement de page) : si le visiteur a déjà
// tapé une question à lui à un moment de l'historique, on ne reproposera plus
// les questions toutes prêtes — `modeLibre` repart sinon à faux à chaque montage.
watch(
  [() => chat.messages.value, () => chat.assistant.value],
  ([msgs, assistant]) => {
    if (modeLibre.value || !assistant) return;
    const questions = new Set(assistant.suggestions.map((q) => q.question));
    if (msgs.some((m) => m.sender_type === "visitor" && !questions.has(m.body ?? "")))
      modeLibre.value = true;
  },
);
watch(
  () => chat.open.value,
  (o) => {
    if (o) {
      // Fil en cours : on montre le dernier message. Sinon on reste en haut,
      // sur l'accueil de l'assistant.
      if (chat.hasConversation.value) scrollToBottom();
      if (showGreeting.value) dismissGreeting();
    }
  },
);

async function toggle() {
  if (chat.open.value) chat.closePanel();
  else await chat.openPanel();
}

/**
 * « Réserver un appel » — ouvre le modal Calendly global (monté une fois dans
 * app.vue). Le panneau de chat se referme d'abord : le modal est plein écran
 * et le recouvrirait, laissant le visiteur avec un panneau invisible mais
 * toujours ouvert derrière.
 *
 * La conversation n'est pas perdue pour autant — `closePanel` ne fait que
 * masquer le panneau, les messages sont rechargés à la réouverture. C'est
 * pourquoi le raccourci reste proposé dans l'en-tête même une fois l'échange
 * engagé.
 */
const { openCalendly } = useCalendlyModal();

function openDemo() {
  chat.closePanel();
  openCalendly();
}

const registerLink = useRegisterLink();

/** Coordonnées déjà transmises à l'équipe : on ne redemande plus. */
const coordonneesDonnees = ref(false);

/**
 * Dès que le visiteur a tapé sa propre question, on arrête de proposer les
 * questions toutes prêtes : elles n'ont plus de rapport avec une conversation
 * qui a pris son propre chemin, et les reproposer casse l'échange en cours.
 */
const modeLibre = ref(false);

async function onSend() {
  const text = draft.value;
  if (!text.trim()) return;
  if (website.value) return; // honeypot rempli → bot, on ignore
  draft.value = "";
  modeLibre.value = true;
  const meta = {
    name: name.value.trim() || undefined,
    email: email.value.trim() || undefined,
  };
  await chat.send(text, meta);
  if (meta.email) coordonneesDonnees.value = true;
}

function isMine(senderType: string) {
  return senderType === "visitor";
}

async function onSuggestion(id: string) {
  if (website.value) return;
  await chat.askSuggestion(id);
}

async function onParlerEquipe() {
  await chat.askTeam({
    name: name.value.trim() || undefined,
    email: email.value.trim() || undefined,
  });
}

/** Questions toutes prêtes pas encore posées dans ce fil (3 au plus, pour
 *  ne pas pousser la réponse qu'on vient de lire hors de l'écran). */
const suggestionsRestantes = computed(() => {
  const posees = new Set(chat.messages.value.map((m) => m.body));
  return (chat.assistant.value?.suggestions ?? [])
    .filter((q) => !posees.has(q.question))
    .slice(0, 3);
});

/** Proposer encore des questions toutes prêtes : seulement tant que le visiteur
 *  n'a lui-même tapé aucune question — après, la conversation suit son propre
 *  fil et les reproposer n'aurait plus de rapport avec l'échange en cours. */
const proposerSuggestions = computed(() => {
  const dernier = chat.messages.value[chat.messages.value.length - 1];
  return (
    !modeLibre.value &&
    !chat.modeEquipe.value &&
    !chat.aiTyping.value &&
    dernier?.sender_type === "ai" &&
    suggestionsRestantes.value.length > 0
  );
});

/** Nom / e-mail : demandés seulement quand c'est l'équipe qui répondra. */
const demanderCoordonnees = computed(
  () => chat.modeEquipe.value && !coordonneesDonnees.value,
);

const config = useRuntimeConfig();
/** URL absolue d'un asset Directus (avatar du membre équipe). */
function assetUrl(idOrPath: string): string {
  // La route backend est montée sur /api/assets — sans le segment « /api »
  // l'URL renvoyait 404 et l'avatar restait cassé (Karim 2026-09-09).
  const base = String(config.public.apiBaseUrl || "").replace(/\/$/, "");
  const clean = idOrPath.replace(/^\/assets\//, "");
  return `${base}/api/assets/${clean}`;
}

/** Un message du visiteur a-t-il été lu par l'équipe (✓✓) ? */
function isReadByStaff(m: { created_at: string }): boolean {
  const readAt = chat.staffReadAt.value;
  return Boolean(readAt && new Date(m.created_at) <= new Date(readAt));
}
</script>

<template>
  <ClientOnly>
    <div class="pcw" :style="styleDecalage">
      <!-- Panneau -->
      <Transition name="pcw-panel">
        <div v-if="chat.open.value" class="pcw__panel">
          <header class="pcw__head">
            <div v-if="!chat.modeEquipe.value" class="pcw__head-title">
              <span class="pcw__head-avatar pcw__head-avatar--ia">
                <DonoorMascotte />
              </span>
              <div>
                <p class="pcw__head-name">{{ $t("partage.chat.assistantNom") }}</p>
                <p class="pcw__head-sub">{{ $t("partage.chat.assistantSous") }}</p>
              </div>
            </div>
            <div v-else class="pcw__head-title">
              <span class="pcw__head-avatar">
                <img
                  v-if="chat.assignedAvatar.value"
                  :src="assetUrl(chat.assignedAvatar.value)"
                  :alt="chat.assignedName.value || $t('partage.chat.equipeDonoor')"
                >
                <span v-else class="pcw__head-avatar-fallback">
                  <UIcon name="i-lucide-headset" />
                </span>
                <span class="pcw__head-presence" :class="{ 'is-online': chat.staffOnline.value }" />
              </span>
              <div>
                <p class="pcw__head-name">{{ chat.assignedName.value || $t("partage.chat.equipeDonoor") }}</p>
                <p class="pcw__head-sub">
                  {{ chat.staffOnline.value ? $t("partage.chat.enLigne") : $t("partage.chat.reponseRapide") }}
                </p>
              </div>
            </div>
            <!-- Actions d'en-tête, à la place de la croix : la fermeture est
                 déjà assurée par la bulle juste en dessous du panneau, deux
                 boutons pour le même geste étaient redondants (Karim
                 2026-09-10). On y met donc les deux autres canaux, qui
                 restent atteignables une fois la conversation engagée — les
                 raccourcis du corps, eux, disparaissent dès le premier
                 message. -->
            <div class="pcw__head-actions">
              <button
                type="button"
                class="pcw__head-action pcw__head-action--wa"
                :aria-label="$t('partage.chat.whatsappAria')"
                title="WhatsApp"
                @click="openWhatsAppContact()"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967c-.273-.099-.471-.148-.67.15c-.197.297-.767.966-.94 1.164c-.173.199-.347.223-.644.075c-.297-.15-1.255-.463-2.39-1.475c-.883-.788-1.48-1.761-1.653-2.059c-.173-.297-.018-.458.13-.606c.134-.133.298-.347.446-.52c.149-.174.198-.298.298-.497c.099-.198.05-.371-.025-.52c-.075-.149-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51a12 12 0 0 0-.57-.01c-.198 0-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074c.149.198 2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625c.712.227 1.36.195 1.872.118c.571-.085 1.758-.719 2.006-1.413c.248-.694.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214l-3.741.982l.998-3.648l-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884c2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.8 11.8 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.9 11.9 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="pcw__head-action pcw__head-action--cal"
                :aria-label="$t('partage.chat.reserverAppelAria')"
                :title="$t('partage.chat.reserverAppel')"
                @click="openDemo"
              >
                <UIcon name="i-lucide-calendar-days" />
              </button>
            </div>
          </header>

          <div ref="scrollEl" class="pcw__body">
            <!-- Accueil de l'assistant IA : message, deux actions, questions toutes
                 prêtes (réponses écrites à l'avance, aucun coût). -->
            <template
              v-if="!chat.hasConversation.value && chat.messages.value.length === 0 && !chat.modeEquipe.value && chat.assistant.value"
            >
              <div class="pcw__msg pcw__msg--them">
                <p class="pcw__bubble">{{ chat.assistant.value.accueil }}</p>
              </div>
              <!-- « Commencer gratuitement » / « Réserver un appel » : ciblent un
                   prospect association, hors sujet sur la fiche d'une cagnotte,
                   adhésion… (`contextuel`) consultée par un donateur/acheteur. -->
              <div v-if="!chat.assistant.value.contextuel" class="pcw__actions-ia">
                <NuxtLink :to="registerLink" class="pcw__action-ia" @click="chat.closePanel()">
                  {{ $t("partage.chat.commencerGratuitement") }}
                </NuxtLink>
                <button type="button" class="pcw__action-ia" @click="openDemo">
                  {{ $t("partage.chat.reserverAppel") }}
                </button>
              </div>
              <div class="pcw__suggestions">
                <button
                  v-for="q in chat.assistant.value.suggestions"
                  :key="q.id"
                  type="button"
                  class="pcw__suggestion"
                  :disabled="chat.sending.value"
                  @click="onSuggestion(q.id)"
                >
                  {{ q.question }}
                </button>
              </div>
            </template>
            <template v-else-if="!chat.hasConversation.value && chat.messages.value.length === 0">
              <!-- Autres canaux — proposés UNIQUEMENT tant qu'aucun échange
                   n'a commencé. Une fois la conversation ouverte, l'équipe
                   répond ici : renvoyer ailleurs disperserait le fil et ferait
                   perdre l'historique au visiteur. -->
              <div class="pcw__channels">
                <button type="button" class="pcw__channel" @click="openWhatsAppContact()">
                  <span class="pcw__channel-icon pcw__channel-icon--wa">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path
                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967c-.273-.099-.471-.148-.67.15c-.197.297-.767.966-.94 1.164c-.173.199-.347.223-.644.075c-.297-.15-1.255-.463-2.39-1.475c-.883-.788-1.48-1.761-1.653-2.059c-.173-.297-.018-.458.13-.606c.134-.133.298-.347.446-.52c.149-.174.198-.298.298-.497c.099-.198.05-.371-.025-.52c-.075-.149-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51a12 12 0 0 0-.57-.01c-.198 0-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074c.149.198 2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625c.712.227 1.36.195 1.872.118c.571-.085 1.758-.719 2.006-1.413c.248-.694.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214l-3.741.982l.998-3.648l-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884c2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.8 11.8 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.9 11.9 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413"
                      />
                    </svg>
                  </span>
                  <span class="pcw__channel-text">
                    <span class="pcw__channel-title">WhatsApp</span>
                    <span class="pcw__channel-sub">{{ $t("partage.chat.reponseRapideCourt") }}</span>
                  </span>
                  <UIcon name="i-lucide-arrow-up-right" class="pcw__channel-go" />
                </button>

                <button type="button" class="pcw__channel" @click="openDemo">
                  <span class="pcw__channel-icon pcw__channel-icon--cal">
                    <UIcon name="i-lucide-calendar-days" />
                  </span>
                  <span class="pcw__channel-text">
                    <span class="pcw__channel-title">{{ $t("partage.chat.reserverAppel") }}</span>
                    <span class="pcw__channel-sub">{{ $t("partage.chat.trenteMinutes") }}</span>
                  </span>
                  <UIcon name="i-lucide-arrow-up-right" class="pcw__channel-go" />
                </button>
              </div>
            </template>
            <div
              v-for="m in chat.messages.value"
              :key="m.id"
              class="pcw__msg"
              :class="
                m.sender_type === 'system'
                  ? 'pcw__msg--system'
                  : isMine(m.sender_type)
                    ? 'pcw__msg--mine'
                    : 'pcw__msg--them'
              "
            >
              <p class="pcw__bubble"><template v-for="(morceau, i) in decouperLiens(m.body)" :key="i"><a v-if="morceau.url" :href="morceau.url" :target="morceau.url.startsWith('mailto:') ? undefined : '_blank'" rel="noopener noreferrer" class="pcw__lien" @click.stop>{{ morceau.texte }}</a><template v-else>{{ morceau.texte }}</template></template></p>
              <UIcon
                v-if="isMine(m.sender_type) && isReadByStaff(m)"
                name="i-lucide-check-check"
                class="pcw__read"
                :title="$t('partage.chat.luParEquipe')"
              />
            </div>

            <!-- « En train d'écrire… » (équipe ou assistant IA) -->
            <div v-if="chat.staffTyping.value || chat.aiTyping.value" class="pcw__msg pcw__msg--them">
              <span class="pcw__typing">
                <span /><span /><span />
              </span>
              <span v-if="chat.aiTyping.value" class="pcw__typing-label">
                {{ $t("partage.chat.assistantEcrit") }}
              </span>
            </div>

            <!-- Après une réponse de l'IA : les autres questions toutes prêtes. -->
            <div v-if="proposerSuggestions" class="pcw__suggestions">
              <button
                v-for="q in suggestionsRestantes"
                :key="q.id"
                type="button"
                class="pcw__suggestion"
                :disabled="chat.sending.value"
                @click="onSuggestion(q.id)"
              >
                {{ q.question }}
              </button>
            </div>

            <p v-if="chat.loading.value" class="pcw__loading">…</p>
          </div>

          <!-- « Parler à l'équipe » : toujours à portée tant que l'IA répond. -->
          <button
            v-if="!chat.modeEquipe.value"
            type="button"
            class="pcw__equipe"
            @click="onParlerEquipe"
          >
            <UIcon name="i-lucide-user-round" />
            {{ $t("partage.chat.parlerEquipe") }}
          </button>

          <!-- Coordonnées optionnelles, demandées quand c'est l'équipe qui répond -->
          <div v-if="demanderCoordonnees" class="pcw__meta-bloc">
            <p v-if="chat.hasConversation.value" class="pcw__meta-aide">
              {{ $t("partage.chat.coordonneesEquipe") }}
            </p>
            <div class="pcw__meta">
              <input v-model="name" class="pcw__input" type="text" :placeholder="$t('partage.chat.nomPlaceholder')" >
              <input v-model="email" class="pcw__input" type="email" :placeholder="$t('partage.chat.emailPlaceholder')" >
            </div>
          </div>

          <form class="pcw__composer" @submit.prevent="onSend">
            <!-- Honeypot : hors écran, non focusable -->
            <input
              v-model="website"
              class="pcw__honeypot"
              type="text"
              tabindex="-1"
              autocomplete="off"
              aria-hidden="true"
            >
            <textarea
              ref="champSaisie"
              v-model="draft"
              class="pcw__textarea"
              rows="1"
              :placeholder="chat.modeEquipe.value ? $t('partage.chat.messagePlaceholder') : $t('partage.chat.questionPlaceholder')"
              @input="chat.notifyTyping()"
              @keydown.enter.exact.prevent="onSend"
            />
            <button
              class="pcw__send"
              type="submit"
              :disabled="!draft.trim() || chat.sending.value"
              :aria-label="$t('commun.actions.envoyer')"
            >
              <UIcon name="i-lucide-send" />
            </button>
          </form>
        </div>
      </Transition>

      <!-- Bulle d'accueil proactive -->
      <Transition name="pcw-greeting">
        <div
          v-if="showGreeting && bulleAutorisee && !chat.open.value"
          class="pcw__greeting"
          @click="toggle"
        >
          <button
            type="button"
            class="pcw__greeting-close"
            :aria-label="$t('partage.chat.fermerMessage')"
            @click.stop="dismissGreeting"
          >
            <UIcon name="i-lucide-x" />
          </button>
          <div class="pcw__greeting-content">
            <span class="pcw__greeting-avatar">
              <DonoorMascotte />
              <span class="pcw__greeting-avatar-dot" />
            </span>
            <p class="pcw__greeting-text">
              {{ $t("partage.chat.bulleAccueil") }}
            </p>
          </div>
        </div>
      </Transition>

      <!-- Bulle flottante -->
      <button
        ref="fabEl"
        class="pcw__fab"
        :class="{
          'pcw__fab--open': chat.open.value,
          'pcw__fab--sur-sombre': surFondSombre,
        }"
        :aria-label="chat.open.value ? $t('partage.chat.fermer') : $t('partage.chat.ouvrir')"
        @click="toggle"
      >
        <!-- Les DEUX icônes sont montées en permanence et superposées : un
             `v-if` échangerait les éléments d'une frame à l'autre, sans état
             intermédiaire à animer. Ici l'une pivote en s'effaçant pendant que
             l'autre arrive en tournant — la bascule se lit comme une
             transformation (Karim 2026-09-10).
             `aria-hidden` sur les deux : le libellé du bouton est porté par
             son `aria-label`, qui suit déjà l'état. -->
        <span class="pcw__fab-icons" aria-hidden="true">
          <!-- Icône lucide "message-circle", remplie (fill) plutôt qu'en
               contour — trop fin en blanc plein sur le rond. Path exact repris
               de @iconify-json/lucide (UIcon la rend en stroke seulement).
               Karim 2026-08-09. -->
          <span class="pcw__fab-icon pcw__fab-icon--chat pcw__mascot">
            <DonoorMascotte
              :regarde-bulle="showGreeting"
              :sombre="!surFondSombre"
              :alerte="alerteMascotte"
              :saute="sautille && !chat.open.value && !showGreeting"
            />
          </span>
          <UIcon name="i-lucide-x" class="pcw__fab-icon pcw__fab-icon--close" />
        </span>
        <span v-if="chat.unread.value > 0 && !chat.open.value" class="pcw__fab-badge">
          {{ chat.unread.value > 9 ? "9+" : chat.unread.value }}
        </span>
        <span
          v-if="chat.unread.value === 0 && !chat.open.value"
          class="pcw__online-dot"
        />
      </button>
    </div>
  </ClientOnly>
</template>

<style scoped>
.pcw {
  position: fixed;
  /* `--pcw-decalage` : place réservée par un élément flottant de la page. */
  bottom: calc(1.5rem + var(--pcw-decalage, 0px));
  right: 1.5rem;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;

  /* Noir plutôt que le bleu de marque dans tout le chat (Karim 2026-09-10).
     Variable locale et non valeur en dur : les huit points de couleur du
     widget (bulle, en-tête, messages, focus, envoi…) doivent rester
     solidaires — c'est leur dispersion qui rend ce genre de changement
     laborieux. `--color-main` reste la couleur de marque du site, elle n'est
     pas touchée ailleurs. */
  --pcw-accent: #0b1020;
  --pcw-accent-soft: #1a2036;
}

/* Chat OUVERT : sa fenêtre passe devant la carte et la barre de don de la
   page cagnotte (z 60) ; fermé, le bouton reste derrière elles (Karim
   2026-10-13). Toujours sous le panneau de don ouvert (z 10011). */
.pcw:has(.pcw__panel) {
  z-index: 65;
}



/* Bulle */
.pcw__fab {
  position: relative;
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  /* Fermé : AUCUN fond ni ombre sur le bouton — c'est la mascotte qui fait
     le rond. Sinon, quand elle sautille ou se secoue, le fond du bouton
     restait en place et dépassait derrière elle. Son ombre la suit (filtre
     sur la mascotte, plus bas). */
  background: transparent;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ouvert : le bouton porte la croix sur un rond plein. Noir sur fond clair,
   blanc sur le hero et le footer (voir `surFondSombre`). */
.pcw__fab--open {
  background: #000;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16), 0 2px 4px rgba(0, 0, 0, 0.1);
}
.pcw__fab--open.pcw__fab--sur-sombre {
  background: #fff;
  color: var(--pcw-accent);
}

.pcw__mascot :deep(svg) {
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.18)) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.1));
  transition: filter 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Survol : la boule grossit comme un objet qu'on peut attraper ; pression : elle s'écrase. */
.pcw__fab:hover {
  transform: translateY(-2px) scale(1.1);
}
.pcw__fab:hover .pcw__mascot :deep(svg) {
  filter: drop-shadow(0 12px 16px rgba(0, 0, 0, 0.22)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.12));
}
.pcw__fab--open:hover {
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24), 0 4px 8px rgba(0, 0, 0, 0.14);
}

.pcw__fab:active {
  transform: scale(0.96);
  transition-duration: 0.15s;
}
/* Les deux icônes occupent la MÊME case, l'une par-dessus l'autre. Sans ce
   conteneur dimensionné, la seconde (en `absolute`) n'aurait pas de repère
   et le bouton se refermerait sur une taille nulle. */
.pcw__fab-icons {
  position: relative;
  width: 1.5rem;
  height: 1.5rem;
}

.pcw__fab-icon {
  position: absolute;
  inset: 0;
  width: 1.5rem;
  height: 1.5rem;
  /* Rotation ET échelle : une simple opacité donnerait un fondu enchaîné,
     pas une transformation. Le quart de tour est ce qui fait lire le passage
     d'un objet à l'autre. */
  transition: opacity 0.2s ease, transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
}

/* Au repos : le chat est visible, la croix attend un quart de tour en arrière
   et rétractée. À l'ouverture, les deux états s'échangent. */
.pcw__fab-icon--close {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

.pcw__fab--open .pcw__fab-icon--chat {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

.pcw__fab--open .pcw__fab-icon--close {
  opacity: 1;
  transform: rotate(0deg) scale(1);
}
.pcw__fab-badge {
  position: absolute;
  top: -0.25rem;
  right: -0.25rem;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 9999px;
  /* Blanc à chiffre rouge : la mascotte devient rouge quand il y a un message,
     une pastille rouge s'y fondrait. */
  background: #fff;
  color: #e5281d;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  font-size: 0.6875rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Panneau */
.pcw__panel {
  /* SORTI DU FLUX, ancré juste au-dessus de la bulle.
     POURQUOI (Karim 2026-09-10) — dans le `flex column` du conteneur, le
     panneau occupait sa hauteur réelle (30rem) même réduit par `scale` : un
     `transform` ne change pas la place réservée dans la mise en page. Le
     bouton se retrouvait donc poussé hors de l'écran pendant l'animation
     puis remontait d'un coup — d'où le panneau qui semblait « venir du bas
     de l'écran » au lieu de sortir du bouton.
     En `absolute`, le conteneur ne réserve plus rien : la bulle ne bouge pas
     d'un pixel et le panneau se déplie depuis son coin bas-droite, c'est-à-
     dire depuis le bouton lui-même. */
  position: absolute;
  right: 0;
  bottom: calc(4rem + 0.75rem);
  width: min(25rem, calc(100vw - 2rem));
  /* Le haut du panneau s'arrête SOUS le header flottant du site (fixe, top
     1.25rem, 3.5rem de haut sur téléphone, 4rem au-delà, z 9999) — avant, il
     montait à 1.25rem du haut et le header passait par-dessus son en-tête
     (Karim 2026-10-16). 6.25rem = bulle + écart en dessous ; `dvh` = hauteur
     VISIBLE sur Safari iPhone (barre d'adresse comprise), `vh` en secours. */
  --pcw-sous-header: 5.5rem;
  height: min(42rem, calc(100vh - 6.25rem - var(--pcw-sous-header) - var(--pcw-decalage, 0px)));
  height: min(42rem, calc(100dvh - 6.25rem - var(--pcw-sous-header) - var(--pcw-decalage, 0px)));
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (min-width: 768px) {
  .pcw__panel {
    --pcw-sous-header: 6rem;
  }
}
.pcw__head {
  background: var(--pcw-accent);
  color: #fff;
  padding: 0.875rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pcw__head-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.pcw__head-avatar {
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
}
.pcw__head-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  object-fit: cover;
}
.pcw__head-avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
/* Pastille de présence : grise par défaut, verte si un membre est en ligne. */
.pcw__head-presence {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 9999px;
  background: #9ca3af;
  border: 2px solid var(--pcw-accent);
}
.pcw__head-presence.is-online {
  background: #34c759;
}
.pcw__head-name {
  font-weight: 700;
  font-size: 0.9375rem;
}
.pcw__head-sub {
  font-size: 0.75rem;
  opacity: 0.75;
}
/* Actions d'en-tête (WhatsApp, Calendly) — la croix a été retirée : la
   fermeture est portée par la bulle, juste sous le panneau. */
.pcw__head-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.pcw__head-action {
  border: none;
  border-radius: 9999px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 2rem : la cible tactile reste confortable sur mobile, alors qu'une
     icône nue de 1rem serait difficile à viser. */
  width: 2rem;
  height: 2rem;
  transition: filter 0.15s ease, transform 0.15s ease;
}

/* Couleurs OFFICIELLES des deux services, et non un fond translucide neutre :
   sur l'en-tête noir, un blanc à 12 % d'opacité rendait les deux boutons
   presque invisibles. Le vert et le bleu de marque les font ressortir et les
   rendent identifiables sans lire l'infobulle — mêmes teintes que les
   pastilles des raccourcis du corps du panneau. */
.pcw__head-action--wa {
  background: #25d366;
}

.pcw__head-action--cal {
  background: #006bff;
}

.pcw__head-action:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

.pcw__head-action svg,
.pcw__head-action :deep(svg) {
  width: 1rem;
  height: 1rem;
}

.pcw__body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: #f7f8fa;
}
/* ── Autres canaux (WhatsApp / Calendly) ──────────────────────────────────
   Visibles uniquement avant la première prise de contact. Volontairement
   sobres — pas de couleur de marque, pas de bouton plein : ce sont des
   options secondaires, le canal principal reste le champ de saisie juste en
   dessous. Un bouton WhatsApp vert vif détournerait du chat lui-même. */
.pcw__channels {
  /* Pas de `margin-top` : le message d'accueil qu'il compensait a été retiré,
     les boutons arrivent maintenant en tête du corps du panneau. */
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pcw__channel {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #eef0f3;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    transform 0.15s ease;
}

.pcw__channel:hover {
  border-color: #d1d5db;
  background: #fafafa;
  transform: translateY(-1px);
}

.pcw__channel-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.pcw__channel-icon svg,
.pcw__channel-icon :deep(svg) {
  width: 1rem;
  height: 1rem;
}

/* Les couleurs officielles des deux services : c'est ce qui les rend
   reconnaissables au premier coup d'œil, sans avoir à lire le libellé. */
.pcw__channel-icon--wa {
  background: #25d366;
}

.pcw__channel-icon--cal {
  background: #006bff;
}

.pcw__channel-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.pcw__channel-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
}

.pcw__channel-sub {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.pcw__channel-go {
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
  color: #d1d5db;
}

.pcw__msg {
  display: flex;
  max-width: 85%;
}
.pcw__msg--mine {
  align-self: flex-end;
}
.pcw__msg--them {
  align-self: flex-start;
}
.pcw__bubble {
  padding: 0.5rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
}
.pcw__msg--mine .pcw__bubble {
  background: var(--pcw-accent);
  color: #fff;
  border-bottom-right-radius: 0.25rem;
}
.pcw__msg--them .pcw__bubble {
  background: #fff;
  color: #1f2937;
  border: 1px solid #eef0f3;
  border-bottom-left-radius: 0.25rem;
}
.pcw__loading {
  align-self: center;
  color: #9ca3af;
}

/* Accusé de lecture ✓✓ sous mes messages */
.pcw__msg--mine {
  flex-direction: column;
  align-items: flex-end;
}
.pcw__read {
  width: 0.9rem;
  height: 0.9rem;
  margin-top: 0.15rem;
  color: var(--pcw-accent);
}

/* Indicateur « en train d'écrire » (3 points) */
.pcw__typing {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.75rem;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 1rem;
  border-bottom-left-radius: 0.25rem;
}
.pcw__typing span {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 9999px;
  background: #9ca3af;
  animation: pcw-typing 1.2s infinite ease-in-out;
}
.pcw__typing span:nth-child(2) { animation-delay: 0.15s; }
.pcw__typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes pcw-typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-0.25rem); opacity: 1; }
}

.pcw__meta {
  display: flex;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem 0;
}
.pcw__input {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  background: #fff;
  color: #1f2937;
}
.pcw__input:focus {
  outline: none;
  border-color: var(--pcw-accent);
}

.pcw__composer {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid #eef0f3;
  background: #fff;
}
.pcw__honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.pcw__textarea {
  flex: 1;
  resize: none;
  max-height: 6rem;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #f7f8fa;
  color: #1f2937;
  font-family: inherit;
}
.pcw__textarea:focus {
  outline: none;
  border-color: var(--pcw-accent);
  background: #fff;
}
.pcw__send {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: var(--pcw-accent);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
}
.pcw__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Ouverture : le panneau se déploie DEPUIS la bulle ────────────────────
   La bulle reste en place — elle devient le bouton de fermeture. Seul le
   panneau est animé, mais il part de la géométrie exacte du rond, ce qui
   donne l'impression qu'il en sort :

     · même origine de transformation (coin bas-droite), et une descente
       jusqu'à la bulle restée juste en dessous ;
     · départ à la taille du rond, avec un rapport d'échelle différent en X
       et en Y — c'est cette déformation qui fait lire une surface qui se
       déplie, pas une boîte qui grossit ;
     · le contenu n'apparaît qu'ensuite, une fois la surface en place. Sans
       ce décalage, le texte se déforme avec le conteneur et l'effet devient
       caoutchouteux.

   `cubic-bezier(0.16, 1, 0.3, 1)` démarre vite et finit en glissant — c'est
   le ralenti final qui donne la sensation de matière. La fermeture est plus
   courte (0,24 s) et sans ralenti : une ouverture se déploie, une fermeture
   libère l'écran sans se faire remarquer.                                  */

.pcw__fab {
  /* Courbe douce sans rebond : la boule grossit et se repose en glissant. */
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.18s ease,
    background-color 0.35s ease, color 0.35s ease;
  transform-origin: center;
  will-change: transform;
}

/* La bulle NE DISPARAÎT PAS à l'ouverture (Karim 2026-09-10) : elle reste en
   place et devient le bouton de fermeture, au même endroit que le bouton
   d'ouverture — le geste est symétrique. Seule l'icône bascule, animée plus
   haut (`.pcw__fab-icon--chat` / `--close`).
   ⚠️ Ne PAS remettre `.pcw__fab-icon` dans ce sélecteur : placé après le bloc
   de bascule, il en écraserait la transition. */
.pcw__fab-badge {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.pcw__fab--open .pcw__fab-badge {
  opacity: 0;
  transform: scale(0.4);
}

/* L'origine du dépliage est le CENTRE DE LA BULLE, pas le coin du panneau.
   Le panneau est ancré 4,75rem plus haut que le bouton (4rem de bulle +
   0,75rem de `gap`) : en poussant l'origine de cette distance SOUS son bord
   inférieur, l'animation prend racine sur le bouton lui-même. Avec une
   origine au simple coin bas-droite, le panneau naissait dans le vide, juste
   au-dessus de la bulle — c'est ce qui donnait l'impression qu'il ne sortait
   pas du bouton (Karim 2026-09-10).

   ⚠️ PAS de `border-radius` animé. Il n'est pas accéléré par le GPU : le
   navigateur redessine la forme à chaque frame, ce qui hachait visiblement
   l'animation. Le rayon reste fixe à 1rem — à l’échelle de départ (0,182) il
   paraît de toute façon très arrondi, donc la transition de forme se lit
   sans qu'on ait à l'animer.
   Ne restent que `transform` et `opacity`, les deux seules propriétés
   composées par le GPU. */
.pcw-panel-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.22s ease-out;
  transform-origin: right calc(100% + 4.75rem);
}

.pcw-panel-leave-active {
  transition: transform 0.2s cubic-bezier(0.4, 0, 1, 1),
    opacity 0.16s ease-in;
  transform-origin: right calc(100% + 4.75rem);
}

.pcw-panel-enter-from,
.pcw-panel-leave-to {
  opacity: 0;
  /* Départ à la taille de la bulle : 4rem sur 25rem de large (0,16) et
     sur 42rem de haut (0,095). Le rapport différent entre X et Y fait lire
     une surface qui se déplie, pas une boîte qui grossit. */
  transform: scale(0.16, 0.095);
}

/* Le contenu attend que la surface soit en place. `delay` à l'entrée
   seulement : à la fermeture il doit partir immédiatement, sinon il flotte
   dans un panneau déjà rétréci. */
.pcw-panel-enter-active .pcw__head,
.pcw-panel-enter-active .pcw__body,
.pcw-panel-enter-active .pcw__meta,
.pcw-panel-enter-active .pcw__composer {
  animation: pcw-content-in 0.22s ease-out 0.18s backwards;
}

@keyframes pcw-content-in {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
}

/* Accessibilité : sans mouvement, ouverture et fermeture sont de simples
   fondus — aucune déformation, aucun étirement. */
@media (prefers-reduced-motion: reduce) {
  .pcw-panel-enter-active,
  .pcw-panel-leave-active {
    transition: opacity 0.15s ease;
  }

  .pcw-panel-enter-from,
  .pcw-panel-leave-to {
    transform: none;
  }

  .pcw-panel-enter-active .pcw__head,
  .pcw-panel-enter-active .pcw__body,
  .pcw-panel-enter-active .pcw__meta,
  .pcw-panel-enter-active .pcw__composer {
    animation: none;
  }

  /* Bascule d'icône en fondu simple : plus de rotation ni de mise à
     l'échelle, mais les deux états restent distincts. */
  .pcw__fab-icon {
    transition: opacity 0.15s ease;
  }

  .pcw__channel:hover {
    transform: none;
  }

  .pcw__fab-icon--close,
  .pcw__fab--open .pcw__fab-icon--chat {
    transform: none;
  }

  .pcw__fab--open .pcw__fab-icon--close {
    transform: none;
  }
}

/* Liens tapés dans un message (donoor.org, https://…, e-mail) : cliquables,
   même couleur que le texte de la bulle (blanc sur la bulle bleue), soulignés.
   Découpage par `decouperLiens` (@donoor/utils/string), jamais de v-html. */
.pcw__lien {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

/* ── Assistant IA ─────────────────────────────────────────────────────────── */

/* La mascotte remplace la photo du membre : pas de fond, elle remplit le rond. */
.pcw__head-avatar--ia :deep(svg) {
  width: 100%;
  height: 100%;
}

/* Deux actions pleines (comme les boutons principaux du site) sous l'accueil. */
.pcw__actions-ia {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.25rem 0 0.25rem;
}

.pcw__action-ia {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  border-radius: 9999px;
  background: var(--pcw-accent);
  color: #fff;
  font-size: 0.75rem;
  white-space: nowrap;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.pcw__action-ia:hover {
  background: var(--pcw-accent-soft);
  transform: translateY(-1px);
}

/* Questions toutes prêtes : pastilles en contour, l'une sous l'autre. */
.pcw__suggestions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
}

.pcw__suggestion {
  max-width: 100%;
  padding: 0.5rem 0.875rem;
  border: 1px solid #dfe3e8;
  border-radius: 1rem;
  background: #fff;
  color: #1f2937;
  font-size: 0.8125rem;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.pcw__suggestion:hover:not(:disabled) {
  border-color: var(--pcw-accent);
  background: #fafafa;
}

.pcw__suggestion:disabled {
  opacity: 0.5;
  cursor: wait;
}

/* Messages système (passage à l'équipe…) : centrés, discrets. */
.pcw__msg--system {
  align-self: center;
  max-width: 92%;
}

.pcw__msg--system .pcw__bubble {
  background: transparent;
  color: #6b7280;
  font-size: 0.75rem;
  text-align: center;
  padding: 0.25rem 0.5rem;
}

.pcw__typing-label {
  margin-left: 0.5rem;
  align-self: center;
  font-size: 0.75rem;
  color: #9ca3af;
}

/* « Parler à l'équipe » : lien discret juste au-dessus de la saisie. */
.pcw__equipe {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  align-self: center;
  /* Même espace au-dessus et en dessous : centré entre le fil et la saisie. */
  margin: 0.4rem auto;
  padding: 0.3rem 0.75rem;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.pcw__equipe:hover {
  background: #f3f4f6;
  color: var(--pcw-accent);
}

.pcw__equipe :deep(svg) {
  width: 0.875rem;
  height: 0.875rem;
}

.pcw__meta-aide {
  padding: 0.625rem 0.75rem 0;
  font-size: 0.75rem;
  color: #6b7280;
}

/* ── Mascotte & accueil ───────────────────────────────────────────────────── */

.pcw__online-dot {
  position: absolute;
  top: 0.1rem;
  right: 0.1rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background: #34c759;
  border: 2px solid #fff;
  animation: pcw-pulse-dot 2.5s ease-in-out infinite;
}

@keyframes pcw-pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.2); }
}

.pcw__fab-icon.pcw__mascot {
  inset: -1.25rem;
  width: 4rem;
  height: 4rem;
}

.pcw__greeting {
  position: absolute;
  right: 0;
  bottom: calc(4rem + 0.75rem);
  width: min(21rem, calc(100vw - 3rem));
  background: #fff;
  border-radius: 1rem;
  border: 2px solid #86efac;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.pcw__greeting:hover {
  border-color: #4ade80;
}

.pcw__greeting-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.pcw__greeting-close:hover {
  color: #4b5563;
  background: #f3f4f6;
}

.pcw__greeting-close :deep(svg) {
  width: 0.875rem;
  height: 0.875rem;
}

.pcw__greeting-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pcw__greeting-avatar {
  flex-shrink: 0;
  position: relative;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pcw__greeting-avatar svg {
  width: 2.75rem;
  height: 2.75rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
}

.pcw__greeting-avatar-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
  background: #ef4444;
  border: 1.5px solid #fff;
}

.pcw__greeting-text {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.45;
  color: #111827;
  padding-right: 1rem;
}

.pcw-greeting-enter-active {
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.pcw-greeting-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.pcw-greeting-enter-from {
  opacity: 0;
  transform: translateY(0.75rem) scale(0.95);
}

.pcw-greeting-leave-to {
  opacity: 0;
  transform: translateY(0.25rem) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .pcw__online-dot {
    animation: none;
  }

  .pcw-greeting-enter-active,
  .pcw-greeting-leave-active {
    transition: opacity 0.15s ease;
  }

  .pcw-greeting-enter-from,
  .pcw-greeting-leave-to {
    transform: none;
  }
}
</style>
